/**
 * 想定最大浸水深(洪水)を座標から取得する。
 *
 * 出典: 国土交通省「不動産情報ライブラリ」洪水浸水想定区域API (XKT026)。
 *   - dev では Vite プロキシ /reinfolib 経由でアクセス (APIキーはサーバ側で注入・秘匿)。
 *   - z=15 タイルの GeoJSON を取得し、対象座標を内包するポリゴンの浸水深ランク
 *     (A31a_205, 1〜9) の最大値を採用する (複数河川が重なる場合は最悪ケース)。
 * ランク→カード表示用の代表値は「区分の上限値」を用いる (防災上の安全側)。
 * 浸水想定区域外は null。
 */
import { fetchWithTimeout } from '@/lib/net/fetch-timeout';
import type { LngLat } from '@/types/property';

export interface FloodDepthResult {
  /** A31a_205 浸水深ランク (1〜9)。 */
  rank: number;
  /** カード表示用の代表値 (例 "3.0m")。区分の上限値。 */
  depthLabel: string;
  /** 想定を出している河川名 (A31a_202)。 */
  rivers: string[];
}

/** 浸水深ランク(想定最大規模, 9段階) → 区分上限の代表値ラベル。 */
const RANK_UPPER_LABEL: Record<number, string> = {
  1: '0.5m', // 0〜0.5m未満
  2: '1.0m', // 0.5〜1.0m
  3: '2.0m', // 1.0〜2.0m
  4: '3.0m', // 2.0〜3.0m
  5: '4.0m', // 3.0〜4.0m
  6: '5.0m', // 4.0〜5.0m
  7: '10.0m', // 5.0〜10.0m
  8: '20.0m', // 10.0〜20.0m
  9: '20.0m', // 20.0m以上
};

const TILE_Z = 15;

function lngToTileX(lng: number, z: number): number {
  return Math.floor(((lng + 180) / 360) * 2 ** z);
}
function latToTileY(lat: number, z: number): number {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.asinh(Math.tan(r)) / Math.PI) / 2) * 2 ** z);
}

/** 点(x,y)がリング内部か (ray casting)。 */
function pointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0]!, yi = ring[i]![1]!;
    const xj = ring[j]![0]!, yj = ring[j]![1]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

interface GeoJsonFeature {
  geometry?: {
    type: string;
    coordinates: unknown;
  } | null;
  properties?: Record<string, unknown> | null;
}

/**
 * 座標の想定最大浸水深を取得する。想定区域外なら null。取得失敗時は throw。
 */
export async function fetchMaxFloodDepth(
  coords: LngLat,
): Promise<FloodDepthResult | null> {
  const x = lngToTileX(coords.lng, TILE_Z);
  const y = latToTileY(coords.lat, TILE_Z);
  const url = `/reinfolib/XKT026?response_format=geojson&z=${TILE_Z}&x=${x}&y=${y}`;
  const res = await fetchWithTimeout(url);
  if (!res.ok) {
    throw new Error(`浸水深APIエラー (HTTP ${res.status})`);
  }
  const gj = (await res.json()) as { features?: GeoJsonFeature[] };

  let maxRank = 0;
  const rivers = new Set<string>();
  for (const f of gj.features ?? []) {
    const g = f.geometry;
    if (!g) continue;
    const polys: number[][][][] =
      g.type === 'Polygon'
        ? [g.coordinates as number[][][]]
        : g.type === 'MultiPolygon'
          ? (g.coordinates as number[][][][])
          : [];
    let contains = false;
    for (const poly of polys) {
      const ring = poly[0];
      if (ring && pointInRing(coords.lng, coords.lat, ring)) {
        contains = true;
        break;
      }
    }
    if (!contains) continue;
    const rank = Number(f.properties?.['A31a_205'] ?? 0);
    if (Number.isFinite(rank) && rank > maxRank) maxRank = rank;
    const river = f.properties?.['A31a_202'];
    if (typeof river === 'string' && river.trim()) rivers.add(river.trim());
  }

  if (maxRank <= 0) return null; // 浸水想定区域外
  return {
    rank: maxRank,
    depthLabel: RANK_UPPER_LABEL[maxRank] ?? `${maxRank}`,
    rivers: [...rivers],
  };
}
